import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormattedDate } from '../../../../../../models/shared/formatted-date';
import { FormValidationUtils } from '../../../../../../utils/form-validation-utils';
import {
  DateSelection,
  DatepickerInputCalendarComponent,
  isClickWithinDatepicker
} from '../../../form-components/datepicker-input/datepicker-input-calendar/datepicker-input-calendar.component';

@Component({
  selector: 'app-review-datepicker-input',
  standalone: true,
  imports: [AsyncPipe, MaskitoDirective, NgbPopover, DatepickerInputCalendarComponent],
  templateUrl: './review-datepicker-input.component.html',
  styleUrls: ['./review-datepicker-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReviewDatepickerInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewDatepickerInputComponent
  implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() minDate: string = '';
  @Input() maxDate: string = '';
  @Input() error: boolean = false;

  @ViewChild('popover') popover!: NgbPopover;
  @ViewChild('dateInput') dateInput!: ElementRef<HTMLInputElement>;

  private elementRef = inject(ElementRef);

  public disabled: boolean = false;
  private resizeObserver!: ResizeObserver;

  public maskitoOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  private _useAbbreviatedDate = new BehaviorSubject<boolean>(false);

  private _showingDatePicker = new BehaviorSubject<boolean>(false);
  public showingDatePicker$ = this._showingDatePicker as Observable<boolean>;

  private _isFocused = new BehaviorSubject<boolean>(false);
  public isFocused$ = this._isFocused as Observable<boolean>;

  private _relevantDate = new BehaviorSubject<FormattedDate | null>(null);
  public relevantDate$ = this._relevantDate.asObservable();

  private _minDate = new BehaviorSubject<string | null>(null);
  public minDate$ = this._minDate.asObservable();

  private _maxDate = new BehaviorSubject<string | null>(null);
  public maxDate$ = this._maxDate.asObservable();

  public selectedFormattedDates$ = this.relevantDate$.pipe(
    map(
      relevantDate =>
        ({
          startDate: relevantDate,
          endDate: null
        } as DateSelection)
    )
  );

  public displayValue$ = combineLatest([
    this.relevantDate$,
    this.showingDatePicker$,
    this.isFocused$,
    this._useAbbreviatedDate
  ]).pipe(
    map(([relevantDate, showingDatepicker, isFocused, useAbbreviatedDate]) => {
      if (!relevantDate) return '';
      if (showingDatepicker || isFocused) return relevantDate.shortHandDate;
      if (!relevantDate.isValid) return relevantDate.shortHandDate;
      return useAbbreviatedDate ? relevantDate.abbreviated : relevantDate.longFormDate;
    })
  );

  public inputIconSrc$ = combineLatest([this.isFocused$, this.showingDatePicker$]).pipe(
    map(([isFocused, showingDatePicker]) => {
      if (this.disabled) return 'assets/icons/disabled-input/Calendar.svg';
      return isFocused || showingDatePicker ? 'assets/icons/dark/Calendar.svg' : 'assets/icons/grey/Calendar.svg';
    })
  );

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.minDate) this._minDate.next(this.minDate);
    if (changes.maxDate) this._maxDate.next(this.maxDate);
  }

  public ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        this._useAbbreviatedDate.next(entry.contentRect.width < 180);
      }
    });
  }

  public ngAfterViewInit(): void {
    this.resizeObserver.observe(this.dateInput.nativeElement);
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    if (isClickWithinDatepicker(event, this.elementRef.nativeElement)) {
      return;
    }
    this.popover?.close();
    this._isFocused.next(false);
    this._showingDatePicker.next(false);
  }

  public writeValue(value: string | null): void {
    if (!value) {
      this._relevantDate.next(null);
      return;
    }
    const selectedDate = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? FormattedDate.fromNativeDateInput(value)
      : FormattedDate.fromUtcString(value);
    this._relevantDate.next(selectedDate);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const selectedDate = value ? FormattedDate.fromShorthand(value) : null;
    if (!selectedDate) {
      this._relevantDate.next(null);
      this.onChange(null);
    } else {
      this._relevantDate.next(selectedDate);
      this.onChange(selectedDate.isValid ? selectedDate.nativeDateInput : FormValidationUtils.INVALID_DATE_MARKER);
    }
  }

  public onFocus(): void {
    this._isFocused.next(true);
    this.openDatePicker();
  }

  public onBlur(): void {
    this._isFocused.next(false);
  }

  public onDateSelected(dateSelection: DateSelection): void {
    const relevantDate = dateSelection.startDate;
    this._relevantDate.next(relevantDate ?? null);
    this.onChange(relevantDate?.nativeDateInput ?? null);
    this.popover.close();
    this._showingDatePicker.next(false);
    this._isFocused.next(false);
  }

  public openDatePicker(): void {
    if (this.disabled) return;
    this._showingDatePicker.next(true);
    this.dateInput.nativeElement.focus();
    this.popover.open();
  }
}
